import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../components/Table";
import { Dropdown } from "react-bootstrap";
import { Button } from "../../controls";
import { usePersistStore, useStore } from "../../store";
import { PaymentService } from "../../services";
import { useMyBidsList } from "../../hooks/queries";
import { Utils } from "../../utils";
import { BID_STATUS } from "../../utils/enums";
import BidStatusDropdown from "../../components/BidStatusDropdown";
import BidUpdatePopup from "../../components/BidUpdatePopup";
import ConfirmPaymentPopup from "../../components/ConfirmPaymentPopup";
import MonthYearFilter from "../../components/MonthYearFilter";
import TableSearchInput from "../../components/TableSearchInput";
import moment from "../../utils/moment";
import { BidStatusChip, StatusChip } from "../../components/status";

function Bids() {
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [month, setMonth] = useState<Date | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage } = useStore((s: any) => s);
  const [showPaymentPopup, setShowPaymentPopup] = useState<boolean>(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [selectedObj, setSelectedObj] = useState<any>(null);

  const { data, isLoading, error, refetch } = useMyBidsList(token, {
    keyword,
    page,
    limit: rowsPerPage,
    status,
    from: month ? moment(month).startOf("month").toISOString() : "",
    to: month ? moment(month).endOf("month").toISOString() : "",
    sorting: "newest-first",
  });
  const list = data?.bids || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const renderPercentageChange = useCallback((row: any) => {
    return Utils.formatPercentChange(row?.adId?.price, row?.price, 2);
  }, []);

  const renderBadge = useCallback(
    (row: any) =>
      row?.status === BID_STATUS.WIN && row?.awaitingBankTransfer ? (
        <StatusChip
          className="statusChip--wide"
          tone="warning"
          icon="bi-bank"
          label="Awaiting transfer"
        />
      ) : (
        <BidStatusChip status={row?.status} />
      ),
    []
  );

  const renderActions = useCallback((row: any) => {
    const status: string = row?.status;

    if (status === BID_STATUS.WIN) {
      // Bank transfer already initiated — the buyer has nothing left to do,
      // so don't show "Pay Now" (the status chip reads "Awaiting bank
      // transfer" instead).
      if (row?.awaitingBankTransfer) return null;

      // A prior payment attempt was cancelled/expired (e.g. a bank transfer
      // Trustap swept). The button still lets them retry, but relabel it and
      // add a note so they understand the earlier attempt didn't complete —
      // otherwise a bare "Pay Now" reads as if they never paid.
      const retryAfterFailure = !!row?.previousPaymentFailed;

      return (
        <div className="d-flex flex-column align-items-center gap-1">
          <Button
            title={retryAfterFailure ? "Pay Again" : "Pay Now"}
            className="btn-sm"
            style={{ borderRadius: 100, minWidth: 80 }}
            onClick={() => {
              setSelectedObj(row);
              setShowPaymentPopup(true);
            }}
          />
          {retryAfterFailure && (
            <span
              className="text-warning"
              style={{ fontSize: 12, lineHeight: 1.2, textAlign: "center" }}
              title="Your previous payment was cancelled and did not go through. You have not been charged."
            >
              Previous payment cancelled
            </span>
          )}
        </div>
      );
    }

    if (status === BID_STATUS.ACTIVE) {
      return (
        <div className="profileDropdown">
          <Dropdown>
            <Dropdown.Toggle aria-label="Row actions">
              <i className="bi bi-three-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  setSelectedObj(row);
                  setShowUpdatePopup(true);
                }}
              >
                <div className="d-flex gap-2 align-items-center">
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                  <span>Edit</span>
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    }

    return null;
  }, []);

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <Link
              to={`/details/${row?.adId?._id}`}
              className="bidsCard__title"
            >
              {row?.adId?.title}
            </Link>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">{renderBadge(row)}</div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.adId?.price)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">My bid</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.price)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Change</span>
            <span className="bidsCard__statValue">
              {renderPercentageChange(row)}
            </span>
          </div>
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-clock" aria-hidden="true"></i>
            <span>Placed {Utils.formatDateTime(row?.createdAt)}</span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-hourglass-split" aria-hidden="true"></i>
            <span>Closes {Utils.formatFromNow(row?.adId?.biddingEnds)}</span>
          </div>
        </div>
        {renderActions(row) && (
          <div className="bidsCard__actions">{renderActions(row)}</div>
        )}
      </div>
    ),
    [renderBadge, renderPercentageChange, renderActions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/details/${row?.adId?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.adId?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.adId?.price),
      },
      {
        title: "Bidding Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.price),
      },
      {
        title: "% Increase",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderPercentageChange(row),
      },
      {
        title: "Bid Placed",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.createdAt),
      },
      {
        title: "Closes in",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatFromNow(row?.adId?.biddingEnds),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row),
      },
      {
        title: "Actions",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderActions(row),
      },
    ],
    [renderPercentageChange, renderBadge, renderActions]
  );

  const handlePayNow = useCallback(
    async (ipAddress: string, paymentMethod: "card" | "bank_transfer") => {
      if (!token || !selectedObj || !selectedObj?._id) return;

      // Reserve a new window *synchronously* inside the user gesture.
      // After any `await`, `window.open` is almost always blocked. We
      // open a blank placeholder now and navigate it once we have the URL.
      const paymentWindow = window.open("about:blank", "_blank");
      if (!paymentWindow) {
        setErrorMessage(
          "Your browser blocked the payment window. Please allow popups for this site and try again."
        );
        return;
      }

      setIsLoading(true);
      let result = await PaymentService.create(token, {
        bidId: selectedObj?._id,
        ipAddress,
        paymentMethod,
      });
      if (result?.error) {
        setErrorMessage(result?.error);
        setIsLoading(false);
        paymentWindow.close();
        return;
      }

      const data: any = result?.data;
      setIsLoading(false);
      if (data?.url) {
        paymentWindow.location.href = data.url;
      } else {
        paymentWindow.close();
        setErrorMessage("Payment URL was not returned. Please try again.");
      }
      // Close the confirm popup now that we've navigated the payment window.
      setShowPaymentPopup(false);
    },
    [token, selectedObj, setErrorMessage, setIsLoading]
  );

  const onStatusChange = useCallback((v: string) => {
    setPage(0);
    setStatus(v);
  }, []);
  const onKeywordChange = useCallback((v: string) => {
    setPage(0);
    setKeyword(v);
  }, []);
  const onMonthChange = useCallback((v: Date | null) => {
    setPage(0);
    setMonth(v);
  }, []);

  return (
    <>
      <BidUpdatePopup
        show={showUpdatePopup}
        onHide={() => {
          setSelectedObj(null);
          setShowUpdatePopup(false);
        }}
        onSuccess={() => {
          setSelectedObj(null);
          setShowUpdatePopup(false);
          refetch();
        }}
        data={selectedObj}
      />
      <ConfirmPaymentPopup
        data={selectedObj}
        show={showPaymentPopup}
        onNo={() => {
          setSelectedObj(null);
          setShowPaymentPopup(false);
        }}
        onYes={(ipAddress, paymentMethod) =>
          handlePayNow(ipAddress, paymentMethod)
        }
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">My Bids</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    {isDone
                      ? `${count} ${count === 1 ? "bid" : "bids"} total`
                      : "Loading your bids…"}
                  </p>
                </div>
                <div className="tableCardHeader__actions customTitle">
                  <BidStatusDropdown value={status} onChange={onStatusChange} />
                  <MonthYearFilter value={month} onChange={onMonthChange} />
                  <TableSearchInput
                    value={keyword}
                    onChange={onKeywordChange}
                  />
                </div>
              </div>
              <Table
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                columns={columns}
                rows={list}
                isDone={isDone}
                count={count}
                mobileCard={renderMobileCard}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Bids;
